import React, { useState, useEffect } from 'react';
import { client, urlFor } from '../../client'; // Import Sanity client and urlFor
import { PortableText } from '@portabletext/react'; // Import PortableText

// Consistent Loading Spinner
const LoadingSpinner = () => (
  <div className="flex justify-center items-center min-h-screen">
    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-[#2a4865]"></div>
    <p className="ml-4 text-lg text-gray-700">Loading...</p>
  </div>
);

// Consistent Error Message
const ErrorMessage = ({ message }) => (
  <div className="container mx-auto px-4 py-12 text-center">
    <p className="text-red-600 font-semibold">{message || 'Error loading page content. Please try again later.'}</p>
  </div>
);

// Custom Portable Text components for support text links/highlights
const ptComponents = {
  marks: {
    link: ({ children, value }) => {
      // For email link
      const rel = !value.href.startsWith('/') ? 'noopener noreferrer' : undefined;
      const className = 'text-yellow-300 hover:text-white font-medium underline';
      return (
        <a href={value.href} target={rel ? '_blank' : undefined} rel={rel} className={className}>
          {children}
        </a>
      );
    },
    highlight: ({ children }) => {
      // For phone number highlight
      const className = 'font-medium text-yellow-300';
      return <span className={className}>{children}</span>;
    },
  },
};

const ParentHandbook = () => {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPageData = async () => {
      setLoading(true);
      try {
        // Fetch page data and the URL for the handbook file
        const query = `*[_type == "parentHandbookPage" && _id == "parentHandbookPageContent"][0] {
          bannerTitle,
          bannerImage {
            asset->{
              _id,
              url
            }
          },
          introText,
          handbookSectionTitle,
          handbookDescription,
          handbookFile {
            asset->{
              url,
              originalFilename
            }
          },
          handbookButtonText,
          supportSectionTitle,
          supportText
        }`;
        const data = await client.fetch(query);
        if (data) {
          setPageData(data);
        } else {
          setError('Parent Handbook Page content not found.');
        }
        setLoading(false);
      } catch (err) {
        console.error("Failed to fetch Parent Handbook page data:", err);
        setError(err.message || 'Failed to load page content. Please try again later.');
        setLoading(false);
      }
    };

    fetchPageData();
  }, []);

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;
  if (!pageData) return <ErrorMessage message="No content available for this page." />;

  // Get the handbook file URL safely
  const handbookUrl = pageData.handbookFile?.asset?.url;

  return (
    <div className="bg-white">
      {/* Hero Banner with Image Overlay - Dynamic */}
      <section className="relative h-[40vh] max-h-[400px]">
        <div className="w-full h-full relative overflow-hidden">
          {pageData.bannerImage?.asset?.url ? (
            <img 
              src={urlFor(pageData.bannerImage).url()} 
              alt={pageData.bannerTitle || 'PS52 Parent Handbook'} 
              className="absolute inset-0 w-full h-full object-cover object-center" 
              loading="lazy"
            />
          ) : (
             <div className="absolute inset-0 w-full h-full bg-gray-300 flex items-center justify-center">
              <span className="text-gray-500">Banner Image Missing</span>
            </div> 
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-black/20"></div>
        </div>
        
        <div className="absolute inset-0 flex items-end">
          <div className="w-full px-6 md:px-12 lg:px-16">
            <div className="max-w-3xl pb-12 md:pb-16 lg:pb-16 text-left">
              <div className="w-16 h-1 bg-yellow-500 mb-6"></div>
              
              <h1 className="text-white text-4xl md:text-5xl font-bold mb-2 leading-tight text-left">
                {pageData.bannerTitle || 'Parent Handbook'} {/* Dynamic */}
              </h1>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="bg-white p-8 rounded-lg shadow-md max-w-4xl mx-auto">
            {/* Introduction - Dynamic */}
            {pageData.introText && (
              <div className="text-lg text-gray-700 leading-relaxed mb-8 prose max-w-none">
                <PortableText value={pageData.introText} />
              </div>
            )}
            
            {/* Handbook Download Section - Dynamic */} 
            {pageData.handbookSectionTitle && (
              <div className="flex flex-col items-center justify-center p-8 bg-[#2a4865]/5 rounded-lg border-t-4 border-[#2a4865]">
                 {/* Keep exact emoji styling */} 
                <div className="text-7xl mb-4">📚</div>
                <h3 className="text-2xl font-bold text-[#2a4865] mb-4">{pageData.handbookSectionTitle}</h3>
                {pageData.handbookDescription && (
                  <p className="text-gray-700 mb-8 text-center max-w-lg">
                    {pageData.handbookDescription}
                  </p>
                )}
                {handbookUrl && pageData.handbookButtonText && (
                  <a 
                    href={handbookUrl} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    // Add download attribute with original filename if available 
                    download={pageData.handbookFile?.asset?.originalFilename}
                    className="bg-[#691e31] text-white py-3 px-8 rounded-full hover:bg-[#7f2a3e] transition-colors inline-flex items-center shadow-lg"
                  >
                    {pageData.handbookButtonText}
                    {/* Keep exact icon */} 
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </a>
                )}
                {!handbookUrl && (
                  <p className="text-red-500 font-medium">Handbook file not available yet.</p> // Fallback if no file URL
                )}
              </div>
            )}
            
            {/* Support Section - Dynamic */} 
            {pageData.supportSectionTitle && pageData.supportText && (
              <div className="bg-[#2a4865] text-white p-6 rounded-lg mt-8">
                <div className="flex items-center mb-4">
                   {/* Keep exact styling */} 
                  <span className="w-1 h-8 bg-yellow-500 mr-3"></span>
                  <h4 className="text-xl font-bold text-white">{pageData.supportSectionTitle}</h4>
                </div>
                <div className="ml-4 leading-relaxed">
                  {/* Use PortableText with custom components */} 
                  <PortableText value={pageData.supportText} components={ptComponents} />
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ParentHandbook; 